/**
 * CV Health copy keys and view models, shared by every `.tsx` / `.web.tsx`
 * twin under components/cv-health and by the onboarding and editor surfaces.
 *
 * Every key returned here lives under `common:cvHealth` in BOTH
 * `i18n/locales/en/common.ts` and `ar/common.ts` (enforced by
 * `__tests__/cv-health-visuals.test.ts`). The server never sends copy: check
 * ids map to keys here. Arithmetic (counts, ring fractions, score) comes from
 * `lib/cv-health-model.ts` and is never repeated in this file.
 *
 * Pure module: no React, no platform code.
 */
import type { TFunction } from 'i18next';
import type {
  CvHealthArea,
  CvHealthCheckId,
  CvHealthCheckStatus,
  CvHealthDecision,
  CvHealthEvidenceSection,
  CvHealthFinding,
  CvHealthReport,
  CvHealthStage,
} from '@tabbio/types';
import {
  CV_HEALTH_AREA_ORDER,
  checkCopyKey,
  countAreaSuggestions,
  countSuggestions,
  findingCopyKey,
  getAreaFindings,
  getCenterValue,
  getNextFinding,
  getRingValues,
  isFindingActionable,
  isReportUsable,
  type CvHealthNotCheckedFamily,
  type CvHealthRingMode,
} from '@/lib/cv-health-model';

type CommonT = TFunction<'common'>;

export type HealthVariant = 'onboarding' | 'editor';

const ROOT = 'cvHealth';

// ─── Plain key helpers ──────────────────────────────────────────────────

export function getAreaLabelKey(area: CvHealthArea): string {
  return `${ROOT}.areas.${area}.label`;
}

export function getAreaPurposeKey(area: CvHealthArea): string {
  return `${ROOT}.areas.${area}.purpose`;
}

export function getAreaAllClearKey(area: CvHealthArea): string {
  return `${ROOT}.areas.${area}.allClear`;
}

/** Short neutral name of a check, used in "About these checks". */
export function getCheckNameKey(checkId: CvHealthCheckId): string {
  return `${ROOT}.checks.${checkCopyKey(checkId)}.name`;
}

export function getCheckTitleKey(checkId: CvHealthCheckId): string {
  return `${ROOT}.checks.${checkCopyKey(checkId)}.title`;
}

export function getFindingTitleKey(finding: CvHealthFinding): string {
  return `${ROOT}.checks.${findingCopyKey(finding)}.title`;
}

export function getFindingWhyKey(finding: CvHealthFinding): string {
  return `${ROOT}.checks.${findingCopyKey(finding)}.why`;
}

export function getFindingChangeKey(finding: CvHealthFinding): string {
  return `${ROOT}.checks.${findingCopyKey(finding)}.change`;
}

/**
 * The finding in the user's own words, one short sentence with no jargon or
 * audience names ("Your CV has no short intro."): the title of a report row.
 */
export function getFindingPlainKey(finding: CvHealthFinding): string {
  return `${ROOT}.checks.${findingCopyKey(finding)}.plain`;
}

/**
 * What happens to it: "We'll ..." when AI or a deterministic repair applies,
 * a gentle instruction when the user has to act. The muted line under a row.
 */
export function getFindingPlainFixKey(finding: CvHealthFinding): string {
  return `${ROOT}.checks.${findingCopyKey(finding)}.plainFix`;
}

/** The sentence Health says about a finding ("Let's add your key skills."). */
export function getFindingAgentKey(finding: CvHealthFinding): string {
  return `${ROOT}.checks.${findingCopyKey(finding)}.agent`;
}

export function getStageTextKey(stage: CvHealthStage): string {
  return `${ROOT}.stages.${stage}`;
}

/** Label of a check status in "About these checks". */
export function getStatusLabelKey(status: CvHealthCheckStatus): string {
  return `${ROOT}.checks.status.${status}`;
}

export function getNotCheckedKey(family: CvHealthNotCheckedFamily): string {
  return `${ROOT}.notChecked.${family}`;
}

const SECTION_LABEL_IDS: Record<CvHealthEvidenceSection, string> = {
  header: 'header',
  contact: 'contact',
  summary: 'summary',
  careerHighlights: 'careerHighlights',
  skills: 'skills',
  experience: 'experience',
  projects: 'projects',
  education: 'education',
  certifications: 'certifications',
  languages: 'languages',
};

/** "Summary", "Experience", ... for the "In your CV · <Section>" quote label. */
export function getSectionLabelKey(section: CvHealthEvidenceSection): string | null {
  const id = SECTION_LABEL_IDS[section];
  return id ? `${ROOT}.actions.sectionLabels.${id}` : null;
}

// ─── Heading ────────────────────────────────────────────────────────────

/** A key plus the plural `count` and the values it needs, when it needs them. */
export interface CvHealthCopyRef {
  key: string;
  count?: number;
  values?: Record<string, number>;
}

export function translateRef(t: CommonT, ref: CvHealthCopyRef): string {
  if (ref.count === undefined && !ref.values) return t(ref.key);
  return t(ref.key, {
    ...ref.values,
    ...(ref.count === undefined ? {} : { count: ref.count }),
  });
}

/**
 * The report heading in plain words: "Your CV scores 72 out of 100". Nothing
 * while checking; the unreadable and failed lines for those reports; nothing
 * when the report has no score (no eligible checks), the sub line still says
 * what to do.
 */
export function getSimpleHeadingRef(
  report: CvHealthReport | null,
  variant: HealthVariant = 'onboarding',
): CvHealthCopyRef | null {
  if (!report || report.status === 'running') return null;
  if (report.status === 'unreadable') return { key: `${ROOT}.heading.unreadable` };
  if (report.status === 'failed') return { key: getStageTextKey('failed') };
  if (typeof report.score !== 'number') return null;
  return {
    key: `${ROOT}.simple.${variant === 'editor' ? 'editorHeading' : 'scoreHeading'}`,
    values: { score: report.score },
  };
}

/**
 * The line under the heading: "3 quick fixes will make it stronger." (plural,
 * `count` = open suggestions, optional links excluded) or "Nothing to fix.
 * Nice work." Nothing while checking or for an unreadable or failed report.
 */
export function getSimpleDetailRef(
  report: CvHealthReport | null,
  variant: HealthVariant = 'onboarding',
): CvHealthCopyRef | null {
  if (!report || !isReportUsable(report)) return null;
  const count = countSuggestions(report);
  const editor = variant === 'editor';
  if (count > 0) {
    return { key: `${ROOT}.simple.${editor ? 'editorFixesSub' : 'fixesSub'}`, count };
  }
  return { key: `${ROOT}.simple.${editor ? 'editorNoFixes' : 'noFixes'}` };
}

/**
 * The report heading. While checking there is none. A result reads "N things
 * to improve" (plural, `count` = open suggestions) or "Your CV looks good".
 */
export function getHeadingKey(report: CvHealthReport | null): CvHealthCopyRef | null {
  if (!report || report.status === 'running') return null;
  if (report.status === 'unreadable') return { key: `${ROOT}.heading.unreadable` };
  if (report.status === 'failed') return { key: getStageTextKey('failed') };
  const count = countSuggestions(report);
  if (count > 0) return { key: `${ROOT}.heading.suggestions`, count };
  return { key: `${ROOT}.heading.looksGood` };
}

/**
 * "Up from 61" / "Down from 84" under the heading, when the score moved since
 * the previous report of the same CV. Nothing when either score is missing or
 * the two are equal.
 */
export function getScoreDeltaRef(
  score: number | null | undefined,
  previousScore: number | null | undefined,
): { key: string; score: number } | null {
  if (typeof score !== 'number' || typeof previousScore !== 'number') return null;
  if (score === previousScore) return null;
  return {
    key: score > previousScore ? `${ROOT}.heading.scoreUpFrom` : `${ROOT}.heading.scoreDownFrom`,
    score: previousScore,
  };
}

/** The quiet line under the heading: partial results or the unreadable hint. */
export function getHeadingDetailKey(report: CvHealthReport | null): string | null {
  if (!report) return null;
  if (report.status === 'unreadable') return `${ROOT}.heading.unreadableBody`;
  if (report.status === 'partial') return `${ROOT}.heading.partial`;
  return null;
}

// ─── Agent sentence ─────────────────────────────────────────────────────

/**
 * The one sentence beside the resting heart: the next outstanding finding's
 * `agent` line, or the all-clear for the area (or the whole CV). Never claims
 * an all-clear for an area whose checks could not run.
 */
export function getAgentSentence(
  t: CommonT,
  report: CvHealthReport | null,
  area?: CvHealthArea,
): string {
  if (!report) return t(getStageTextKey('reading'));
  if (report.status === 'running') return t(getStageTextKey(report.stage));
  if (report.status === 'failed') return t(getStageTextKey('failed'));
  if (report.status === 'unreadable') return t(`${ROOT}.heading.unreadable`);

  const next = getNextFinding(report, area);
  if (next) return t(getFindingAgentKey(next));

  if (area) {
    if (report.areas[area]?.status === 'unavailable') {
      return t(`${ROOT}.heading.areaUnavailable`);
    }
    // Only an optional recommendation is left: say that, not "all clear".
    const optional = getAreaFindings(report, area).find((finding) => !isFindingActionable(finding));
    if (optional) return t(getFindingAgentKey(optional));
    return t(getAreaAllClearKey(area));
  }
  return t(`${ROOT}.heading.looksGood`);
}

// ─── Findings ───────────────────────────────────────────────────────────

const SEVERITY_RANK: Record<CvHealthFinding['severity'], number> = {
  high: 0,
  medium: 1,
  low: 2,
  optional: 3,
};

/** Open findings of an area, highest severity first, report order within. */
export function getSortedAreaFindings(
  report: CvHealthReport,
  area: CvHealthArea,
): CvHealthFinding[] {
  return getAreaFindings(report, area)
    .map((finding, index) => ({ finding, index }))
    .sort(
      (a, b) =>
        SEVERITY_RANK[a.finding.severity] - SEVERITY_RANK[b.finding.severity] ||
        a.index - b.index,
    )
    .map(({ finding }) => finding);
}

/** Highest severity first, report order within a severity. */
function sortBySeverity(report: CvHealthReport, findings: CvHealthFinding[]): CvHealthFinding[] {
  const order = new Map(report.findings.map((finding, index) => [finding.key, index]));
  return [...findings].sort(
    (a, b) =>
      SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] ||
      (order.get(a.key) ?? 0) - (order.get(b.key) ?? 0),
  );
}

/**
 * Every open finding of the report, flat (no audience grouping): highest
 * severity first, report order within, so the optional professional link
 * always comes last. The list the onboarding report and the editor show.
 */
export function getSortedOpenFindings(report: CvHealthReport): CvHealthFinding[] {
  return sortBySeverity(
    report,
    report.findings.filter((finding) => finding.decision === null),
  );
}

/**
 * Whether "Fix with AI" includes a finding: open, with a proposal, and not a
 * question. `check_detail` findings ask about an ambiguous fact and are never
 * corrected in bulk; optional recommendations never count as a suggestion.
 */
export function isFixableFinding(finding: CvHealthFinding): boolean {
  return (
    finding.decision === null &&
    finding.proposal !== null &&
    finding.kind !== 'check_detail' &&
    finding.severity !== 'optional'
  );
}

/** The findings one "Fix N things with AI" applies, in list order. */
export function getFixableFindings(report: CvHealthReport | null): CvHealthFinding[] {
  if (!report) return [];
  return sortBySeverity(report, report.findings.filter(isFixableFinding));
}

/** Up to `max` findings named under an audience row. */
export function getAreaHighlights(
  report: CvHealthReport,
  area: CvHealthArea,
  max = 2,
): CvHealthFinding[] {
  return getSortedAreaFindings(report, area).slice(0, max);
}

// ─── Badges ─────────────────────────────────────────────────────────────

export type HealthBadgeTone = 'attention' | 'good' | 'neutral';

export interface HealthAreaBadge {
  tone: HealthBadgeTone;
  count: number;
  /** Accessible label; pass `count` when set. */
  label: CvHealthCopyRef;
}

/**
 * The trailing badge of an audience row. Nothing while checking; a neutral
 * dash when the area couldn't be checked; the warm count for open
 * suggestions; a check disc once the area looks good.
 */
export function getAreaBadge(report: CvHealthReport, area: CvHealthArea): HealthAreaBadge | null {
  const summary = report.areas[area];
  if (!summary || report.status === 'running' || summary.status === 'checking') return null;
  if (summary.status === 'unavailable') {
    return { tone: 'neutral', count: 0, label: { key: `${ROOT}.badges.unavailable` } };
  }
  const count = countAreaSuggestions(report, area);
  if (count > 0) {
    return { tone: 'attention', count, label: { key: `${ROOT}.badges.suggestions`, count } };
  }
  if (summary.status === 'looks_good') {
    return { tone: 'good', count: 0, label: { key: `${ROOT}.badges.looksGood` } };
  }
  return null;
}

// ─── Rings accessibility ────────────────────────────────────────────────

export function getRingsAccessibilityLabel(
  t: CommonT,
  report: CvHealthReport | null,
  mode: CvHealthRingMode,
): string {
  const center = getCenterValue(report, mode);
  if (center.kind === 'percent') {
    return t(`${ROOT}.rings.progress`, { percent: center.value });
  }
  if (center.kind === 'score') {
    const rings = getRingValues(report, mode);
    const ring = (area: CvHealthArea) =>
      rings.find((value) => value.area === area) ?? { value: 0, eligible: 0 };
    return t(`${ROOT}.rings.score`, {
      score: center.value,
      humanPassed: ring('human').value,
      humanEligible: ring('human').eligible,
      systemPassed: ring('system').value,
      systemEligible: ring('system').eligible,
      aiPassed: ring('ai_bot').value,
      aiEligible: ring('ai_bot').eligible,
    });
  }
  return t(`${ROOT}.rings.unavailable`);
}

// ─── Finding actions ────────────────────────────────────────────────────

/**
 * What an action does when pressed:
 * - `preview`  opens the editable proposal review (editor, `onPreviewChange`)
 * - `reveal`   expands the proposal preview inline, read-only (onboarding)
 * - `decision` records a decision (`onDecision`)
 * - `proof`    starts adding a professional link (`onAddProofLink`)
 */
export type HealthFindingActionIntent = 'preview' | 'reveal' | 'decision' | 'proof';

export interface HealthFindingAction {
  id: string;
  labelKey: string;
  emphasis: 'primary' | 'text';
  intent: HealthFindingActionIntent;
  decision?: CvHealthDecision;
}

export interface HealthFindingHandlersAvailable {
  preview: boolean;
  decision: boolean;
  proof: boolean;
}

const ACTION_ROOT = `${ROOT}.actions`;

function proposalAction(
  variant: HealthVariant,
  editorLabel: string,
  emphasis: HealthFindingAction['emphasis'],
): HealthFindingAction {
  return variant === 'editor'
    ? { id: 'preview', labelKey: `${ACTION_ROOT}.${editorLabel}`, emphasis, intent: 'preview' }
    : { id: 'reveal', labelKey: `${ACTION_ROOT}.seeChange`, emphasis, intent: 'reveal' };
}

function decisionAction(
  decision: CvHealthDecision,
  label: string,
  emphasis: HealthFindingAction['emphasis'] = 'text',
): HealthFindingAction {
  return { id: `decision-${decision}`, labelKey: `${ACTION_ROOT}.${label}`, emphasis, intent: 'decision', decision };
}

/**
 * The actions under an expanded finding, by kind. `variant` only changes
 * labels and what a proposal action does. Actions whose handler the caller
 * did not provide are left out (`reveal` needs none).
 */
export function getFindingActions(
  finding: CvHealthFinding,
  variant: HealthVariant,
  available: HealthFindingHandlersAvailable,
): HealthFindingAction[] {
  const hasProposal = finding.proposal !== null;
  const actions: HealthFindingAction[] = [];
  switch (finding.kind) {
    case 'improve_text':
      if (hasProposal) actions.push(proposalAction(variant, 'previewChange', 'primary'));
      actions.push(decisionAction('kept', 'leaveUnchanged'));
      break;
    case 'add_detail':
      if (hasProposal) actions.push(proposalAction(variant, 'previewChange', 'primary'));
      actions.push(decisionAction('skipped', 'skipForNow'));
      break;
    case 'check_detail':
      actions.push(decisionAction('kept', 'looksRight'));
      if (hasProposal) actions.push(proposalAction(variant, 'useSuggestion', 'text'));
      break;
    case 'personal_detail':
      actions.push(decisionAction('left_out', 'leaveOut', 'primary'));
      actions.push(decisionAction('kept', 'keepIt'));
      break;
    case 'professional_proof':
      actions.push({
        id: 'proof',
        labelKey: `${ACTION_ROOT}.addProfessionalLink`,
        emphasis: 'primary',
        intent: 'proof',
      });
      actions.push(decisionAction('skipped', 'noProfessionalLink'));
      break;
    case 'fix_link':
      if (hasProposal) actions.push(proposalAction(variant, 'useFixedLink', 'primary'));
      actions.push(decisionAction('kept', 'leaveUnchanged'));
      break;
    case 'explanation':
    default:
      break;
  }
  return actions.filter((action) => {
    if (action.intent === 'preview') return available.preview;
    if (action.intent === 'decision') return available.decision;
    if (action.intent === 'proof') return available.proof;
    return true;
  });
}

/** Every check family the report lists for an area, in report order. */
export function getAreaChecks(report: CvHealthReport, area: CvHealthArea) {
  return report.checks.filter((check) => check.area === area);
}

/** Every check of the report grouped by audience, in display order (areas with no checks are left out). */
export function getChecksByArea(report: CvHealthReport) {
  return CV_HEALTH_AREA_ORDER.map((area) => ({ area, checks: getAreaChecks(report, area) })).filter(
    (group) => group.checks.length > 0,
  );
}

/** Areas in display order, re-exported for renderers. */
export const HEALTH_AREA_ORDER = CV_HEALTH_AREA_ORDER;

// ─── Gauge report (30 Sep 2026 design) ──────────────────────────────────

/** Short tab label of an area ("Recruiters", "Systems", "AI"). */
export function getAreaTabKey(area: CvHealthArea): string {
  return `${ROOT}.areas.${area}.tab`;
}

/**
 * The status beside an area under "What we checked": "Looks good",
 * "2 improvements" (open suggestions, optional links excluded) or
 * "Couldn't check" when none of its checks could run.
 */
export function getAreaStatusRef(report: CvHealthReport, area: CvHealthArea): CvHealthCopyRef {
  const summary = report.areas[area];
  if (!summary || summary.status === 'unavailable' || summary.eligible === 0) {
    return { key: `${ROOT}.report.couldntCheck` };
  }
  const count = countAreaSuggestions(report, area);
  if (count > 0) return { key: `${ROOT}.report.improvements`, count };
  return { key: `${ROOT}.report.looksGood` };
}

/**
 * The rows under "View individual suggestions": every open finding except
 * optional recommendations (the professional link lives in its own card on
 * the AI tools tab), highest severity first. `area` narrows to one tab.
 */
export function getSuggestionFindings(
  report: CvHealthReport | null,
  area?: CvHealthArea,
): CvHealthFinding[] {
  if (!report || !isReportUsable(report)) return [];
  return getSortedOpenFindings(report).filter(
    (finding) => finding.severity !== 'optional' && (!area || finding.area === area),
  );
}

/** The open optional professional-link recommendation, if the report has one. */
export function getProofFinding(report: CvHealthReport | null): CvHealthFinding | null {
  if (!report) return null;
  return (
    report.findings.find(
      (finding) => finding.kind === 'professional_proof' && finding.decision === null,
    ) ?? null
  );
}

export interface HealthAreaCheckRow {
  id: CvHealthCheckId;
  passed: boolean;
  nameKey: string;
  statusKey: string;
}

/**
 * "Checks in this area": each check that applies to this CV with its plain
 * outcome. Checks that do not apply are left out; the optional professional
 * link never counts, so it is not listed as a check either.
 */
export function getAreaCheckRows(report: CvHealthReport, area: CvHealthArea): HealthAreaCheckRow[] {
  return getAreaChecks(report, area)
    .filter((check) => check.status !== 'not_applicable' && check.id !== 'ai_bot.professional_proof')
    .map((check) => ({
      id: check.id,
      passed: check.status === 'passed',
      nameKey: getCheckNameKey(check.id),
      statusKey: getStatusLabelKey(check.status),
    }));
}

/** The review list's label for one change: what it will do, in a few words. */
export function getChangeLabelKey(finding: CvHealthFinding): string {
  return getFindingTitleKey(finding);
}
